import * as opentypeModule from "opentype.js";
import montserrat800 from "@fontsource/montserrat/files/montserrat-latin-800-normal.woff";
import montserrat500 from "@fontsource/montserrat/files/montserrat-latin-500-normal.woff";
import poppins600 from "@fontsource/poppins/files/poppins-latin-600-normal.woff";
import poppins400 from "@fontsource/poppins/files/poppins-latin-400-normal.woff";
import playfair700 from "@fontsource/playfair-display/files/playfair-display-latin-700-normal.woff";
import cinzel700 from "@fontsource/cinzel/files/cinzel-latin-700-normal.woff";
import cinzel400 from "@fontsource/cinzel/files/cinzel-latin-400-normal.woff";
import oswald600 from "@fontsource/oswald/files/oswald-latin-600-normal.woff";
import oswald400 from "@fontsource/oswald/files/oswald-latin-400-normal.woff";
import raleway800 from "@fontsource/raleway/files/raleway-latin-800-normal.woff";
import raleway500 from "@fontsource/raleway/files/raleway-latin-500-normal.woff";
import lora700 from "@fontsource/lora/files/lora-latin-700-normal.woff";
import lora400 from "@fontsource/lora/files/lora-latin-400-normal.woff";
import bebas400 from "@fontsource/bebas-neue/files/bebas-neue-latin-400-normal.woff";
import pacifico400 from "@fontsource/pacifico/files/pacifico-latin-400-normal.woff";
import dancing700 from "@fontsource/dancing-script/files/dancing-script-latin-700-normal.woff";
import grotesk700 from "@fontsource/space-grotesk/files/space-grotesk-latin-700-normal.woff";
import grotesk400 from "@fontsource/space-grotesk/files/space-grotesk-latin-400-normal.woff";
import josefin700 from "@fontsource/josefin-sans/files/josefin-sans-latin-700-normal.woff";
import josefin400 from "@fontsource/josefin-sans/files/josefin-sans-latin-400-normal.woff";
import abril400 from "@fontsource/abril-fatface/files/abril-fatface-latin-400-normal.woff";
import righteous400 from "@fontsource/righteous/files/righteous-latin-400-normal.woff";
import inter500 from "@fontsource/inter/files/inter-latin-500-normal.woff";

const opentype = opentypeModule.default || opentypeModule;

// key, label, file, the moods it suits. "name" fonts carry the brand name; "text" fonts carry the tagline.
export const FONTS = {
  montserrat: { label: "Montserrat", role: "name", url: montserrat800, moods: ["modern", "bold", "tech"], css: "Montserrat" },
  poppins: { label: "Poppins", role: "name", url: poppins600, moods: ["modern", "playful", "minimal"], css: "Poppins" },
  raleway: { label: "Raleway", role: "name", url: raleway800, moods: ["minimal", "elegant", "modern"], css: "Raleway" },
  josefin: { label: "Josefin Sans", role: "name", url: josefin700, moods: ["minimal", "elegant", "vintage"], css: "Josefin Sans" },
  grotesk: { label: "Space Grotesk", role: "name", url: grotesk700, moods: ["tech", "modern", "bold"], css: "Space Grotesk" },
  oswald: { label: "Oswald", role: "name", url: oswald600, moods: ["bold", "vintage", "sport"], css: "Oswald" },
  bebas: { label: "Bebas Neue", role: "name", url: bebas400, moods: ["bold", "sport", "vintage"], css: "Bebas Neue" },
  playfair: { label: "Playfair Display", role: "name", url: playfair700, moods: ["elegant", "luxury", "vintage"], css: "Playfair Display" },
  cinzel: { label: "Cinzel", role: "name", url: cinzel700, moods: ["luxury", "elegant", "vintage"], css: "Cinzel" },
  lora: { label: "Lora", role: "name", url: lora700, moods: ["elegant", "vintage", "minimal"], css: "Lora" },
  abril: { label: "Abril Fatface", role: "name", url: abril400, moods: ["luxury", "bold", "vintage"], css: "Abril Fatface" },
  righteous: { label: "Righteous", role: "name", url: righteous400, moods: ["playful", "tech", "bold"], css: "Righteous" },
  pacifico: { label: "Pacifico (script)", role: "name", url: pacifico400, moods: ["playful", "vintage"], css: "Pacifico" },
  dancing: { label: "Dancing Script", role: "name", url: dancing700, moods: ["elegant", "playful", "luxury"], css: "Dancing Script" },
  inter: { label: "Inter", role: "text", url: inter500, moods: ["modern", "minimal", "tech", "bold"], css: "Inter" },
  montserratText: { label: "Montserrat", role: "text", url: montserrat500, moods: ["modern", "bold", "playful"], css: "Montserrat" },
  ralewayText: { label: "Raleway", role: "text", url: raleway500, moods: ["minimal", "elegant", "modern"], css: "Raleway" },
  poppinsText: { label: "Poppins", role: "text", url: poppins400, moods: ["playful", "modern"], css: "Poppins" },
  josefinText: { label: "Josefin Sans", role: "text", url: josefin400, moods: ["elegant", "vintage", "minimal"], css: "Josefin Sans" },
  groteskText: { label: "Space Grotesk", role: "text", url: grotesk400, moods: ["tech"], css: "Space Grotesk" },
  oswaldText: { label: "Oswald", role: "text", url: oswald400, moods: ["bold", "vintage", "sport"], css: "Oswald" },
  loraText: { label: "Lora", role: "text", url: lora400, moods: ["elegant", "vintage", "luxury"], css: "Lora" },
  cinzelText: { label: "Cinzel", role: "text", url: cinzel400, moods: ["luxury", "elegant"], css: "Cinzel" },
};

export const NAME_FONTS = Object.keys(FONTS).filter((k) => FONTS[k].role === "name");
export const TEXT_FONTS = Object.keys(FONTS).filter((k) => FONTS[k].role === "text");

const parsed = {};
const loading = {};

export const fontLoaded = (key) => !!parsed[key];
export const getFont = (key) => parsed[key] || null;

// Gives the engine an already parsed font (tests do this with files read from disk).
export function registerFont(key, font) {
  parsed[key] = font;
}

export function loadFont(key) {
  if (parsed[key]) return Promise.resolve(parsed[key]);
  const entry = FONTS[key];
  if (!entry) return Promise.reject(new Error(`Unknown font ${key}`));
  if (!loading[key]) {
    loading[key] = fetch(entry.url)
      .then((response) => {
        if (!response.ok) throw new Error(`Font ${key} could not be loaded`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        parsed[key] = opentype.parse(buffer);
        return parsed[key];
      })
      .finally(() => { delete loading[key]; });
  }
  return loading[key];
}

export const loadFonts = (keys) => Promise.all([...new Set(keys)].map(loadFont));

// A text font that goes with a name font: the pairing is picked by mood.
export function pairedTextFont(nameKey) {
  const moods = FONTS[nameKey]?.moods || ["modern"];
  const fit = TEXT_FONTS.find((k) => FONTS[k].moods.includes(moods[0]));
  return fit || "inter";
}
